import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { LOCAL_KEYS } from '../constants/storageKeys'
import { safeLocalStorage } from '../utils/safeStorage'
import deResources from './locales/de/index.js'

export const SUPPORTED_LANGUAGES = [
	{ code: 'de', label: 'Deutsch', flagCode: 'de' },
	{ code: 'en', label: 'English', flagCode: 'gb' },
	{ code: 'fr', label: 'Français', flagCode: 'fr' },
	{ code: 'it', label: 'Italiano', flagCode: 'it' },
	{ code: 'ro', label: 'Română', flagCode: 'ro' },
	{ code: 'ru', label: 'Русский', flagCode: 'ru' },
	{ code: 'uk', label: 'Українська', flagCode: 'ua' },
	{ code: 'be', label: 'Беларуская', flagCode: 'by' },
	{ code: 'pl', label: 'Polski', flagCode: 'pl' },
	{ code: 'tr', label: 'Türkçe', flagCode: 'tr' },
	{ code: 'ar', label: 'العربية', flagCode: 'sa' },
	{ code: 'fa', label: 'فارسی', flagCode: 'ir' },
	{ code: 'he', label: 'עברית', flagCode: 'il' },
	{ code: 'ka', label: 'ქართული', flagCode: 'ge' },
]

const NAMESPACES = [
	'common',
	'startPage',
	'legal',
	'finish',
	'sectionA',
	'sectionB',
	'sectionC',
	'sectionD',
	'sectionE',
	'sectionF',
	'sectionG',
	'sectionH',
	'sectionI',
	'sectionJ',
	'sectionK',
	'sectionL',
	'sectionReview',
	'hinweisblatt',
]

const FALLBACK_LANGUAGE = 'de'

const savedLanguage = safeLocalStorage.getItem(LOCAL_KEYS.LANGUAGE)
const initialLanguage = SUPPORTED_LANGUAGES.some(l => l.code === savedLanguage)
	? savedLanguage
	: FALLBACK_LANGUAGE

const loadedLanguages = new Set([FALLBACK_LANGUAGE])

const LANGUAGE_LOADERS = {
	en: () => import('./locales/en/index.js'),
	ru: () => import('./locales/ru/index.js'),
	tr: () => import('./locales/tr/index.js'),
	uk: () => import('./locales/uk/index.js'),
	ar: () => import('./locales/ar/index.js'),
	he: () => import('./locales/he/index.js'),
	pl: () => import('./locales/pl/index.js'),
	fr: () => import('./locales/fr/index.js'),
	be: () => import('./locales/be/index.js'),
	ka: () => import('./locales/ka/index.js'),
	ro: () => import('./locales/ro/index.js'),
	fa: () => import('./locales/fa/index.js'),
	it: () => import('./locales/it/index.js'),
}

const loadLanguage = lang =>
	lang === FALLBACK_LANGUAGE
		? Promise.resolve(deResources)
		: LANGUAGE_LOADERS[lang]().then(m => m.default)

export const ensureLanguageLoaded = async lang => {
	if (loadedLanguages.has(lang)) return
	const bundle = await loadLanguage(lang)
	NAMESPACES.forEach(ns => {
		i18next.addResourceBundle(lang, ns, bundle[ns], true, true)
	})
	loadedLanguages.add(lang)
}

export const initI18n = async () => {
	const needsFallback = initialLanguage !== FALLBACK_LANGUAGE

	const resources = { [FALLBACK_LANGUAGE]: deResources }
	let startLanguage = initialLanguage
	if (needsFallback) {
		try {
			resources[initialLanguage] = await loadLanguage(initialLanguage)
			loadedLanguages.add(initialLanguage)
		} catch (error) {
			// The saved language's chunk could not be fetched; start in German rather
			// than leaving the user on the loading screen.
			console.error(`Could not load language "${initialLanguage}"`, error)
			startLanguage = FALLBACK_LANGUAGE
		}
	}

	await i18next.use(initReactI18next).init({
		resources,
		lng: startLanguage,
		fallbackLng: FALLBACK_LANGUAGE,
		ns: NAMESPACES,
		defaultNS: 'common',
		interpolation: { escapeValue: false },
		returnEmptyString: false,
	})
}

i18next.on('languageChanged', lng => {
	safeLocalStorage.setItem(LOCAL_KEYS.LANGUAGE, lng)
})

export default i18next
